import { useAuth } from "@clerk/clerk-react";
import { Box, ButtonBase, IconButton, Typography } from "@mui/material";
import { getMarkRange } from "@tiptap/core";
import { Editor as TipTapEditor } from "@tiptap/react";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";

import { OrganizationTemplateRubricSuggestion } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";

import styles from "./styles";

interface SocialProofSuggestionPopupProps {
  editor: TipTapEditor;
  suggestion: OrganizationTemplateRubricSuggestion;
  onApplied?: (suggestionId: number, beforeHtml: string) => void;
  onDismiss: () => void | Promise<void>;
}

const SocialProofSuggestionPopup = ({
  editor,
  suggestion,
  onApplied,
  onDismiss,
}: SocialProofSuggestionPopupProps) => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const [url, setUrl] = useState("");
  const [whyOpen, setWhyOpen] = useState(false);

  const handlePaste = async (): Promise<void> => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setUrl(text.trim());
    } catch {
      // Clipboard read can be blocked; user can paste manually.
    }
  };

  const handleApply = async (): Promise<void> => {
    const href = normalizeUrl(url);
    if (!href || !currentOrg?.id) return;

    const highlightMark = editor.state.schema.marks.highlight;
    if (!highlightMark) return;
    const { from } = editor.state.selection;
    const $pos = editor.state.doc.resolve(from);
    const range = getMarkRange($pos, highlightMark);
    if (!range) return;

    const text = editor.state.doc.textBetween(range.from, range.to);

    const beforeHtml = editor.getHTML();

    editor
      .chain()
      .focus()
      .setTextSelection({ from: range.from, to: range.to })
      .deleteSelection()
      .insertContent({
        type: "text",
        text,
        marks: [{ type: "link", attrs: { href, target: "_blank" } }],
      })
      .run();

    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg.id}/organization-template-rubric-suggestions/suggestion/${suggestion.id}`,
      "PATCH",
      {},
      { status: "applied" },
    );

    onApplied?.(suggestion.id, beforeHtml);
  };

  const hasUrl = normalizeUrl(url) !== null;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.head}>
        <Box sx={styles.avatar}>
          <Box
            component="i"
            className="fa-solid fa-paperclip"
            sx={styles.avatarIcon}
          />
        </Box>
        <Box sx={styles.helperBlock}>
          <Typography sx={styles.helperText}>Bento helper</Typography>
          <Box sx={styles.kindRow}>
            <Box component="span" sx={styles.kindDot} />
            Tip · Social proof
          </Box>
        </Box>
        <IconButton size="small" sx={styles.closeButton} onClick={onDismiss}>
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={styles.closeIcon}
          />
        </IconButton>
      </Box>

      <Box sx={styles.body}>
        {suggestion.title && (
          <Typography sx={styles.title}>{suggestion.title}</Typography>
        )}
        <Typography sx={styles.rationale}>{suggestion.body}</Typography>

        <Box sx={styles.inputWrapper}>
          <Box
            component="i"
            className="fa-solid fa-link"
            sx={styles.inputIcon}
          />
          <Box
            component="input"
            type="text"
            value={url}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setUrl(e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter" && hasUrl) handleApply();
            }}
            placeholder="https://instagram.com/reel/..."
            sx={styles.input}
          />
          <ButtonBase sx={styles.pasteButton} onClick={handlePaste}>
            <Box
              component="i"
              className="fa-regular fa-clipboard"
              sx={styles.pasteButtonIcon}
            />
            Paste
          </ButtonBase>
        </Box>
      </Box>

      {whyOpen && (
        <Box sx={styles.whyBlock}>
          <Typography sx={styles.whyHead}>Why this matters</Typography>
          When you cite a number without proof, brand managers tend to Google or
          skip it. A linked post gives them something to actually look at and
          tends to lift reply rates.
        </Box>
      )}

      <Box sx={styles.actions}>
        {hasUrl ? (
          <ButtonBase sx={styles.primaryButton} onClick={handleApply}>
            <Box
              component="i"
              className="fa-solid fa-check"
              sx={styles.primaryIcon}
            />
            Link it
          </ButtonBase>
        ) : (
          <Box sx={styles.primaryButtonDisabled}>
            <Box
              component="i"
              className="fa-solid fa-check"
              sx={styles.primaryIcon}
            />
            Paste a link to continue
          </Box>
        )}
        <ButtonBase sx={styles.rejectButton} onClick={onDismiss}>
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={styles.rejectIcon}
          />
        </ButtonBase>
        <ButtonBase
          sx={styles.whyButton}
          onClick={() => setWhyOpen((prev) => !prev)}
        >
          <Box
            component="i"
            className="fa-regular fa-circle-info"
            sx={styles.whyIcon}
          />
          {whyOpen ? "Hide" : "Why?"}
        </ButtonBase>
      </Box>
    </Box>
  );
};

function normalizeUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const withProtocol = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const parsed = new URL(withProtocol);
    if (!parsed.hostname.includes(".")) return null;
    return parsed.href;
  } catch {
    return null;
  }
}

export default SocialProofSuggestionPopup;
